import { useState } from 'react';

import {
  useInvitations,
  useInviteUser,
  useOrgRoles,
  useOrgUsers,
  useRevokeInvitation,
  useUpdateUserRoles,
  useUpdateUserStatus,
} from '../api/hooks';
import type { InvitationView, OrgUserView } from '../api/types';
import {
  Badge,
  Button,
  DataTable,
  FormField,
  Modal,
  Select,
  StatusPill,
  TextInput,
  useToast,
} from '../components/DesignSystem';
import type { DataTableColumn } from '../components/DesignSystem';
import { PencilIcon, TrashIcon } from '../components/icons';
import { PageHeader } from '../components/PageHeader';
import { errorMessage } from '../lib/errors';
import { formatDate } from '../lib/format';

type ModalState =
  | { kind: 'none' }
  | { kind: 'invite' }
  | { kind: 'roles'; user: OrgUserView }
  | { kind: 'status'; user: OrgUserView };

const STATUS_TONES = {
  active: 'success',
  invited: 'warning',
  disabled: 'danger',
} as const;

/** `/org/users` — admin-only membership, roles, and invitations. */
export function UserAccessPage() {
  const { showToast } = useToast();
  const users = useOrgUsers({ limit: 100 });
  const roles = useOrgRoles();
  const invitations = useInvitations();

  const inviteUser = useInviteUser();
  const revokeInvitation = useRevokeInvitation();
  const updateRoles = useUpdateUserRoles();
  const updateStatus = useUpdateUserStatus();

  const [modal, setModal] = useState<ModalState>({ kind: 'none' });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);

  const roleList = roles.data ?? [];
  const roleNameOf = (roleId: string) =>
    roleList.find((role) => role.id === roleId)?.name ?? roleId.slice(0, 8);

  async function handleInvite() {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail) || inviteRoleId === '') {
      showToast('A valid email and a role are required', 'error');
      return;
    }

    try {
      await inviteUser.mutateAsync({ email: inviteEmail, roleId: inviteRoleId });
      showToast(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRoleId('');
      setModal({ kind: 'none' });
    } catch (error) {
      showToast(errorMessage(error, 'Could not send invitation'), 'error');
    }
  }

  async function handleSaveRoles(user: OrgUserView) {
    if (selectedRoleIds.length === 0) {
      showToast('Users need at least one role', 'error');
      return;
    }

    try {
      await updateRoles.mutateAsync({ userId: user.id, roleIds: selectedRoleIds });
      showToast('Roles updated');
      setModal({ kind: 'none' });
    } catch (error) {
      showToast(errorMessage(error, 'Could not update roles'), 'error');
    }
  }

  async function handleToggleStatus(user: OrgUserView) {
    const nextStatus = user.status === 'disabled' ? 'active' : 'disabled';

    try {
      await updateStatus.mutateAsync({ userId: user.id, status: nextStatus });
      showToast(nextStatus === 'disabled' ? 'User disabled' : 'User re-enabled');
      setModal({ kind: 'none' });
    } catch (error) {
      showToast(errorMessage(error, 'Could not update user status'), 'error');
    }
  }

  async function handleRevoke(invitation: InvitationView) {
    try {
      await revokeInvitation.mutateAsync(invitation.id);
      showToast('Invitation revoked');
    } catch (error) {
      showToast(errorMessage(error, 'Could not revoke invitation'), 'error');
    }
  }

  const userColumns: DataTableColumn<OrgUserView>[] = [
    { key: 'displayName', header: 'Name' },
    { key: 'email', header: 'Email' },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusPill tone={STATUS_TONES[row.status]}>{row.status}</StatusPill>,
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (row) => (
        <span className="rfp-badge-row">
          {row.roles.map((role) => (
            <Badge key={role.id} tone={role.name === 'admin' ? 'primary' : 'teal'}>
              {role.name}
            </Badge>
          ))}
        </span>
      ),
    },
    { key: 'lastLoginAt', header: 'Last Login', render: (row) => formatDate(row.lastLoginAt) },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <span className="rfp-icon-row">
          <button
            aria-label={`Edit roles for ${row.displayName}`}
            className="rfp-iconbtn"
            onClick={() => {
              setSelectedRoleIds(row.roles.map((role) => role.id));
              setModal({ kind: 'roles', user: row });
            }}
            title="Edit roles"
            type="button"
          >
            <PencilIcon />
          </button>
          <button
            aria-label={
              row.status === 'disabled' ? `Enable ${row.displayName}` : `Disable ${row.displayName}`
            }
            className="rfp-iconbtn rfp-iconbtn--danger"
            onClick={() => setModal({ kind: 'status', user: row })}
            title={row.status === 'disabled' ? 'Enable user' : 'Disable user'}
            type="button"
          >
            <TrashIcon />
          </button>
        </span>
      ),
    },
  ];

  const pendingInvitations = (invitations.data ?? []).filter(
    (invitation) => invitation.status === 'pending',
  );

  const invitationColumns: DataTableColumn<InvitationView>[] = [
    { key: 'email', header: 'Email' },
    { key: 'role', header: 'Role', render: (row) => roleNameOf(row.roleId) },
    { key: 'status', header: 'Status' },
    { key: 'expiresAt', header: 'Expires', render: (row) => formatDate(row.expiresAt) },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <Button onClick={() => handleRevoke(row)} type="button" variant="ghost">
          Revoke
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        actions={
          <Button onClick={() => setModal({ kind: 'invite' })} type="button">
            Invite User
          </Button>
        }
        caption="Manage who can access this organisation."
        title="User Access"
      />

      <section className="rfp-card">
        <div className="rfp-card__head">
          <h2 className="rfp-card__title">Members ({users.data?.data.length ?? 0})</h2>
        </div>
        {users.isLoading ? (
          <p className="rfp-loading">Loading users…</p>
        ) : users.isError ? (
          <p className="rfp-inline-error" role="alert">
            Could not load users — please retry.
          </p>
        ) : (
          <DataTable
            columns={userColumns}
            emptyMessage="No users yet"
            rowKey={(row) => row.id}
            rows={users.data?.data ?? []}
          />
        )}
      </section>

      <section className="rfp-card">
        <div className="rfp-card__head">
          <h2 className="rfp-card__title">Pending Invitations ({pendingInvitations.length})</h2>
        </div>
        <DataTable
          columns={invitationColumns}
          emptyMessage="No pending invitations"
          rowKey={(row) => row.id}
          rows={pendingInvitations}
        />
      </section>

      {modal.kind === 'invite' && (
        <Modal
          actions={
            <>
              <Button disabled={inviteUser.isPending} onClick={handleInvite} type="button">
                {inviteUser.isPending ? 'Sending…' : 'Send Invitation'}
              </Button>
              <Button onClick={() => setModal({ kind: 'none' })} type="button" variant="secondary">
                Cancel
              </Button>
            </>
          }
          title="Invite User"
        >
          <div className="rfp-stack">
            <FormField htmlFor="invite-email" label="Email">
              <TextInput
                id="invite-email"
                onChange={(event) => setInviteEmail(event.target.value)}
                placeholder="colleague@studio.com"
                type="email"
                value={inviteEmail}
              />
            </FormField>
            <FormField htmlFor="invite-role" label="Role">
              <Select
                id="invite-role"
                onChange={(event) => setInviteRoleId(event.target.value)}
                value={inviteRoleId}
              >
                <option value="">Select a role…</option>
                {roleList.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                    {role.description ? ` — ${role.description}` : ''}
                  </option>
                ))}
              </Select>
            </FormField>
          </div>
        </Modal>
      )}

      {modal.kind === 'roles' && (
        <Modal
          actions={
            <>
              <Button
                disabled={updateRoles.isPending}
                onClick={() => handleSaveRoles(modal.user)}
                type="button"
              >
                {updateRoles.isPending ? 'Saving…' : 'Save Roles'}
              </Button>
              <Button onClick={() => setModal({ kind: 'none' })} type="button" variant="secondary">
                Cancel
              </Button>
            </>
          }
          title={`Roles — ${modal.user.displayName}`}
        >
          <div className="rfp-check-grid">
            {roleList.map((role) => (
              <label className="rfp-check" key={role.id}>
                <input
                  checked={selectedRoleIds.includes(role.id)}
                  onChange={() =>
                    setSelectedRoleIds((current) =>
                      current.includes(role.id)
                        ? current.filter((id) => id !== role.id)
                        : [...current, role.id],
                    )
                  }
                  type="checkbox"
                />
                {role.name}
              </label>
            ))}
          </div>
        </Modal>
      )}

      {modal.kind === 'status' && (
        <Modal
          actions={
            <>
              <Button
                disabled={updateStatus.isPending}
                onClick={() => handleToggleStatus(modal.user)}
                type="button"
              >
                {modal.user.status === 'disabled' ? 'Enable' : 'Disable'}
              </Button>
              <Button onClick={() => setModal({ kind: 'none' })} type="button" variant="secondary">
                Cancel
              </Button>
            </>
          }
          title={modal.user.status === 'disabled' ? 'Enable user?' : 'Disable user?'}
        >
          <p>
            {modal.user.status === 'disabled'
              ? `${modal.user.displayName} will regain access to this organisation.`
              : `${modal.user.displayName} will immediately lose access to this organisation.`}
          </p>
        </Modal>
      )}
    </>
  );
}
